import type { ReactElement } from 'react';
import { Box, Button, type TableColumns } from '@ff-it/ui';
import { ProductList } from './ProductList';
import { disabledProductsSummary } from './ProductList.css';
import type { ProductKindSectionMeta } from './kindSections';

type ProductSectionRow = {
  product: { disabled: boolean };
};

export type ProductKindSectionProps<TRow extends ProductSectionRow> = {
  meta: ProductKindSectionMeta;
  count: number;
  onCreate?: () => void;
  rows: TRow[];
  columns: TableColumns<TRow>;
  getRowId: (row: TRow) => string;
};

/** Shared kind-section chrome for bounded product collections. */
export function ProductKindSection<TRow extends ProductSectionRow>({
  meta,
  count,
  onCreate,
  rows,
  columns,
  getRowId,
}: ProductKindSectionProps<TRow>): ReactElement {
  const activeRows = rows.filter(({ product }) => !product.disabled);
  const disabledRows = rows.filter(({ product }) => product.disabled);

  return (
    <Box px="4" py="3" borderBottom="default">
      <Box display="flex" alignItems="baseline" gap="3" py="2">
        <Box as="h3" my="0" fontWeight="medium">
          {meta.title}
        </Box>
        <Box fontSize="xs" color="gray600">
          {count}
        </Box>
        <Box flexGrow={1} />
        {onCreate && (
          <Button variant="secondary" onClick={onCreate}>
            {meta.createLabel}
          </Button>
        )}
      </Box>

      {rows.length === 0 ? (
        <ProductList
          rows={rows}
          columns={columns}
          getRowId={getRowId}
          emptyState={meta.emptyLabel}
          ariaLabel={meta.title}
        />
      ) : (
        <>
          {activeRows.length > 0 && (
            <ProductList
              rows={activeRows}
              columns={columns}
              getRowId={getRowId}
              emptyState={meta.emptyLabel}
              ariaLabel={meta.title}
            />
          )}

          {disabledRows.length > 0 && (
            <Box as="details" mt="3">
              <Box as="summary" className={disabledProductsSummary}>
                Disabled products ({disabledRows.length})
              </Box>
              <ProductList
                rows={disabledRows}
                columns={columns}
                getRowId={getRowId}
                emptyState={meta.emptyLabel}
                ariaLabel={`${meta.title}, disabled`}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
